import React, { useState, useEffect, useCallback } from 'react';

const API_BASE = 'https://surabaya-api.onrender.com/api';

const COLORS = {
  brun: '#4A2C17',
  or: '#E8C47C',
  cream: '#FDF6EC',
  gris: '#8C7B6E',
  grisClair: '#F5EDE3',
  blanc: '#FFFFFF',
  rouge: '#C0392B',
  vert: '#27AE60',
};

const SOURCE_LABEL = {
  link: { texte: 'Lien de désinscription', icone: '🔗' },
  reply: { texte: 'Réponse STOP', icone: '↩️' },
  manual: { texte: 'Ajout manuel', icone: '✍️' },
  bounce: { texte: 'Adresse morte', icone: '📭' },
  complaint: { texte: 'Signalé en spam', icone: '🚫' },
};

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })
    + ' à ' + d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

export default function DesinscriptionsModal({ token, onClose }) {
  const [data, setData] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState('');
  const [email, setEmail] = useState('');
  const [motif, setMotif] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [message, setMessage] = useState('');

  const headers = useCallback(() => ({
    'Content-Type': 'application/json',
    ...(token ? { Authorization: 'Bearer ' + token } : {}),
  }), [token]);

  const charger = useCallback(async () => {
    setChargement(true);
    setErreur('');
    try {
      const r = await fetch(API_BASE + '/admin/optout', { headers: headers() });
      if (!r.ok) throw new Error('HTTP ' + r.status);
      const j = await r.json();
      setData(j);
    } catch (e) {
      setErreur('Impossible de charger la liste (' + e.message + ')');
    } finally {
      setChargement(false);
    }
  }, [headers]);

  useEffect(() => { charger(); }, [charger]);

  const ajouter = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setEnvoi(true);
    setMessage('');
    try {
      const r = await fetch(API_BASE + '/admin/optout', {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({ email: email.trim(), reason: motif.trim() || undefined }),
      });
      const j = await r.json();
      if (!r.ok || !j.ok) throw new Error(j.error || 'HTTP ' + r.status);
      setMessage(j.message);
      setEmail('');
      setMotif('');
      charger();
    } catch (err) {
      setMessage('Erreur : ' + err.message);
    } finally {
      setEnvoi(false);
    }
  };

  const reinscrire = async (ligne) => {
    const ok = window.confirm(
      'Réinscrire ' + ligne.email + ' ?\n\n'
      + "À ne faire que si la personne le redemande explicitement, ou si c'est une erreur de saisie. "
      + "Réinscrire quelqu'un qui s'est désinscrit lui-même n'est pas légal."
    );
    if (!ok) return;
    try {
      const r = await fetch(API_BASE + '/admin/optout/' + ligne.id, { method: 'DELETE', headers: headers() });
      const j = await r.json();
      if (!r.ok || !j.ok) throw new Error(j.error || 'HTTP ' + r.status);
      setMessage(ligne.email + ' réinscrite');
      charger();
    } catch (err) {
      setMessage('Erreur : ' + err.message);
    }
  };

  const st = {
    overlay: {
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,.6)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    },
    modal: {
      background: COLORS.cream, borderRadius: 14, width: '100%', maxWidth: 720,
      maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden',
      boxShadow: '0 20px 60px rgba(0,0,0,.35)',
    },
    header: {
      background: COLORS.brun, color: COLORS.or, padding: '16px 20px',
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    },
    corps: { padding: 20, overflowY: 'auto' },
    stat: {
      display: 'flex', gap: 12, marginBottom: 18, flexWrap: 'wrap',
    },
    carte: {
      flex: 1, minWidth: 150, background: COLORS.blanc, borderRadius: 10,
      padding: '12px 14px', border: '1px solid ' + COLORS.grisClair,
    },
    chiffre: { fontSize: 26, fontWeight: 800, color: COLORS.brun },
    libelle: { fontSize: 12, color: COLORS.gris, textTransform: 'uppercase', letterSpacing: 0.5 },
    form: {
      background: COLORS.blanc, borderRadius: 10, padding: 14,
      border: '1px solid ' + COLORS.grisClair, marginBottom: 18,
    },
    input: {
      width: '100%', padding: '10px 12px', borderRadius: 8, marginBottom: 8,
      border: '1px solid ' + COLORS.grisClair, fontSize: 14, fontFamily: 'inherit',
    },
    btn: {
      background: COLORS.brun, color: COLORS.or, border: 'none', borderRadius: 8,
      padding: '10px 18px', fontWeight: 700, cursor: 'pointer', fontSize: 14,
    },
    ligne: {
      display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10,
      background: COLORS.blanc, borderRadius: 8, padding: '10px 12px', marginBottom: 6,
      border: '1px solid ' + COLORS.grisClair,
    },
    lienRetirer: {
      background: 'transparent', border: '1px solid ' + COLORS.rouge, color: COLORS.rouge,
      borderRadius: 6, padding: '5px 10px', cursor: 'pointer', fontSize: 12, whiteSpace: 'nowrap',
    },
  };

  return (
    <div style={st.overlay} onClick={onClose}>
      <div style={st.modal} onClick={(e) => e.stopPropagation()}>
        <div style={st.header}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 800 }}>✉️ Désinscriptions</div>
            <div style={{ fontSize: 12, opacity: 0.75 }}>Qui ne reçoit plus les campagnes</div>
          </div>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: COLORS.or, fontSize: 26, cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>

        <div style={st.corps}>
          {chargement && <div style={{ color: COLORS.gris }}>Chargement…</div>}
          {erreur && <div style={{ color: COLORS.rouge, marginBottom: 12 }}>{erreur}</div>}

          {data && (
            <>
              <div style={st.stat}>
                <div style={st.carte}>
                  <div style={st.chiffre}>{data.total_destinataires}</div>
                  <div style={st.libelle}>Destinataires</div>
                </div>
                <div style={st.carte}>
                  <div style={{ ...st.chiffre, color: COLORS.rouge }}>{data.total_optouts}</div>
                  <div style={st.libelle}>Désinscrits</div>
                </div>
              </div>

              <form style={st.form} onSubmit={ajouter}>
                <div style={{ fontWeight: 700, color: COLORS.brun, marginBottom: 8, fontSize: 14 }}>
                  Désinscrire quelqu'un à la main
                </div>
                <div style={{ fontSize: 12, color: COLORS.gris, marginBottom: 10 }}>
                  Pour une demande reçue par téléphone, au comptoir, ou par réponse « STOP ».
                </div>
                <input
                  style={st.input}
                  type="email"
                  placeholder="adresse@exemple.fr"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
                <input
                  style={st.input}
                  type="text"
                  placeholder="Motif (facultatif) — ex : demande au téléphone le 20/08"
                  value={motif}
                  onChange={(e) => setMotif(e.target.value)}
                />
                <button style={{ ...st.btn, opacity: envoi ? 0.6 : 1 }} disabled={envoi} type="submit">
                  {envoi ? 'Enregistrement…' : 'Désinscrire'}
                </button>
                {message && (
                  <div style={{ marginTop: 10, fontSize: 13, color: message.startsWith('Erreur') ? COLORS.rouge : COLORS.vert }}>
                    {message}
                  </div>
                )}
              </form>

              <div style={{ fontWeight: 700, color: COLORS.brun, marginBottom: 8, fontSize: 14 }}>
                Liste ({data.optouts.length})
              </div>
              {data.optouts.length === 0 && (
                <div style={{ color: COLORS.gris, fontSize: 14 }}>Personne pour l'instant.</div>
              )}
              {data.optouts.map((o) => {
                const src = SOURCE_LABEL[o.source] || { texte: o.source, icone: '•' };
                return (
                  <div key={o.id} style={st.ligne}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 600, color: COLORS.brun, wordBreak: 'break-all' }}>{o.email}</div>
                      <div style={{ fontSize: 12, color: COLORS.gris }}>
                        {src.icone} {src.texte} · {formatDate(o.created_at)}
                        {o.reason ? ' · ' + o.reason : ''}
                      </div>
                    </div>
                    <button style={st.lienRetirer} onClick={() => reinscrire(o)}>Réinscrire</button>
                  </div>
                );
              })}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
